import { Col, Form, Row } from 'antd';
import { useMemo } from 'react';

import { ERROR_MESSAGE, MINMAX_NUMBER_FORM_RULE } from '../../../utils/constants';
import FormItem from '../../Form/FormItem';
import Select from '../../Select';
import SubmitCancelButtonGroup from '../../SubmitCancelButtonGroup';
import HorizontalSwitch from '../../Switch/HorizontalSwitch';
import Text from '../../Text';
import TextInput from '../../TextInput';

export const DEFAULT_GENERATION_CONFIG = Object.freeze({
  temperature: 0.1,
  topK: 32,
  topP: 1,
  maxOutputTokens: 8192,
});

const MODEL_OPTIONS = [{ value: 'gemini-2.5-flash', label: 'gemini-2.5-flash' }];
const FORMAT_OPTIONS = [{ value: 'text', label: 'text' }];

const PromptForm = props => {
  const { t, isUpdate, loading, disabled, initialValues, onFinish, onCancel } = props;
  const [form] = Form.useForm();

  const TYPE_OPTIONS = useMemo(
    () => [
      { value: 'insurance-compliance-validation', label: t('insurance-compliance-validation') },
      { value: 'vehicle-insurance-extraction', label: t('vehicle-insurance-extraction') },
    ],
    [t],
  );

  const numberFieldRules = [
    { required: true, message: ERROR_MESSAGE().BLANK_FIELD },
    {
      validator: (_, value) => {
        if (value === undefined || value === null || value === '') {
          return Promise.resolve();
        }

        return Number.isNaN(Number(value))
          ? Promise.reject(ERROR_MESSAGE().NUMBER_FIELD)
          : Promise.resolve();
      },
    },
  ];
  const temperatureFieldRules = [...numberFieldRules, ...MINMAX_NUMBER_FORM_RULE(0, 1)];

  const handleSubmit = async values => {
    const payload = {
      ...values,
      generationConfig: {
        temperature: Number(values.generationConfig.temperature),
        topK: Number(values.generationConfig.topK),
        topP: Number(values.generationConfig.topP),
        maxOutputTokens: Number(values.generationConfig.maxOutputTokens),
      },
    };

    await onFinish(payload);
  };

  return (
    <Form
      form={form}
      disabled={disabled}
      onFinish={handleSubmit}
      initialValues={{
        model: 'gemini-2.5-flash',
        type: 'insurance-compliance-validation',
        isActive: true,
        format: 'text',
        generationConfig: { ...DEFAULT_GENERATION_CONFIG },
        ...initialValues,
      }}
    >
      <FormItem
        name="name"
        label={t('name')}
        rules={[{ required: true, message: ERROR_MESSAGE().BLANK_FIELD }]}
      >
        <TextInput />
      </FormItem>

      <FormItem
        name="description"
        label={t('description')}
        rules={[{ required: true, message: ERROR_MESSAGE().BLANK_FIELD }]}
      >
        <TextInput textArea autoSize={{ minRows: 2, maxRows: 6 }} />
      </FormItem>

      <Row gutter={16}>
        <Col xs={24} md={8}>
          <FormItem
            name="model"
            label={t('model')}
            rules={[{ required: true, message: ERROR_MESSAGE().BLANK_FIELD }]}
          >
            <Select resetOnNoMatch={false} options={MODEL_OPTIONS} />
          </FormItem>
        </Col>

        <Col xs={24} md={8}>
          <FormItem
            name="type"
            label={t('type')}
            rules={[{ required: true, message: ERROR_MESSAGE().BLANK_FIELD }]}
          >
            <Select resetOnNoMatch={false} options={TYPE_OPTIONS} />
          </FormItem>
        </Col>

        <Col xs={24} md={8}>
          <FormItem
            name="format"
            label={t('format')}
            rules={[{ required: true, message: ERROR_MESSAGE().BLANK_FIELD }]}
          >
            <Select resetOnNoMatch={false} options={FORMAT_OPTIONS} />
          </FormItem>
        </Col>
      </Row>

      <FormItem name="isActive" valuePropName="checked">
        <HorizontalSwitch label={t('active')} size="medium" />
      </FormItem>

      <FormItem
        name="text"
        label={t('text')}
        rules={[{ required: true, message: ERROR_MESSAGE().BLANK_FIELD }]}
      >
        <TextInput textArea autoSize={{ minRows: 8, maxRows: 14 }} />
      </FormItem>

      <Text strong style={{ marginBottom: 10 }}>
        {t('generationConfig')}
      </Text>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <FormItem
            name={['generationConfig', 'temperature']}
            label={t('temperature')}
            rules={temperatureFieldRules}
          >
            <TextInput type="number" />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem name={['generationConfig', 'topK']} label={t('topK')} rules={numberFieldRules}>
            <TextInput type="number" />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem name={['generationConfig', 'topP']} label={t('topP')} rules={numberFieldRules}>
            <TextInput type="number" />
          </FormItem>
        </Col>

        <Col xs={24} md={12}>
          <FormItem
            name={['generationConfig', 'maxOutputTokens']}
            label={t('maxOutputTokens')}
            rules={numberFieldRules}
          >
            <TextInput type="number" />
          </FormItem>
        </Col>
      </Row>

      <SubmitCancelButtonGroup
        isForm
        onCancel={onCancel}
        disabled={disabled}
        loading={loading}
        submitText={t(isUpdate ? 'update' : 'create')}
      />
    </Form>
  );
};

PromptForm.defaultProps = {
  isUpdate: false,
  loading: false,
  disabled: false,
  initialValues: {},
  onFinish: () => {},
  onCancel: () => {},
};

export default PromptForm;
